import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  Search, 
  Plus, 
  Filter, 
  Download,
  Eye,
  Edit,
  MoreHorizontal,
  UserCheck,
  UserX,
  Shield,
  Mail,
  Phone,
  Calendar,
  Building2,
  MapPin,
  Crown,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Star,
  Activity,
  Ban,
  Unlock,
  Key,
  Settings,
  Trash2,
  RefreshCw,
  UserPlus,
  FileText,
  Globe,
  Target
} from 'lucide-react';

const UltraUsers = () => {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companySearch, setCompanySearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;
  const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const [usersRes, orgsRes] = await Promise.allSettled([
        axios.get(`${API}/api/ultra/users?limit=200`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/ultra/organizations?limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const usersRaw = usersRes.status === 'fulfilled' ? (usersRes.value.data.items || []) : [];
      const orgsRaw  = orgsRes.status  === 'fulfilled' ? (orgsRes.value.data.items  || []) : [];

      // Map orgs as "companies" for the left panel
      const companies = orgsRaw.map(o => ({
        id: o.id,
        name: o.display_name || o.legal_name || o.id,
        type: o.org_type || 'UNKNOWN',
        status: (o.status || 'active').toLowerCase(),
        plan: o.plan_code || 'dynamic',
        user_count: 0, // will be calculated below
        active_users: 0,
        created_at: o.created_at ? o.created_at.slice(0, 10) : '',
      }));

      // Map real users
      const mappedUsers = usersRaw.map(u => ({
        id: u.id,
        company_id: u.org_memberships?.[0]?.org_id || null,
        first_name: u.first_name || '',
        last_name: u.last_name || '',
        email: u.email,
        role: u.role || '',
        status: u.is_active ? 'active' : 'suspended',
        last_login: u.last_login_at || null,
        created_at: u.created_at ? u.created_at.slice(0, 10) : '',
        password_visible: false,
      }));

      // Count users per company
      mappedUsers.forEach(u => {
        const comp = companies.find(c => c.id === u.company_id);
        if (comp) {
          comp.user_count++;
          if (u.status === 'active') comp.active_users++;
        }
      });

      setCompanies(companies);
      setUsers(mappedUsers);
      if (companies.length > 0 && !selectedCompany) setSelectedCompany(companies[0]);
    } catch (error) {
      console.error('Fetch data error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter companies
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = companySearch === '' || company.name.toLowerCase().includes(companySearch.toLowerCase());
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    const matchesPlan = planFilter === 'all' || company.plan === planFilter;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  // Filter users for selected company
  const filteredUsers = users.filter(user => {
    if (!selectedCompany) return false;
    const belongsToCompany = user.company_id === selectedCompany.id;
    const matchesSearch = userSearch === '' ||
      (user.first_name + ' ' + user.last_name).toLowerCase().includes(userSearch.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
      (user.role || '').toLowerCase().includes(userSearch.toLowerCase());
    return belongsToCompany && matchesSearch;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Aktif', variant: 'default', icon: CheckCircle2, color: 'bg-green-100 text-green-800' },
      trial: { label: 'Deneme', variant: 'secondary', icon: Clock, color: 'bg-yellow-100 text-yellow-800' },
      suspended: { label: 'Askıda', variant: 'destructive', icon: Ban, color: 'bg-red-100 text-red-800' },
      pending: { label: 'Beklemede', variant: 'outline', icon: AlertCircle, color: 'bg-blue-100 text-blue-800' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`gap-1 ${config.color} border-0 hover:${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      'ULTRA_ADMIN': { label: 'Ultra Admin', color: 'bg-purple-100 text-purple-800', icon: Crown },
      'CONTRACTOR_ADMIN': { label: 'Contractor Admin', color: 'bg-blue-100 text-blue-800', icon: Shield },
      'EXHIBITOR_ADMIN': { label: 'Exhibitor Admin', color: 'bg-green-100 text-green-800', icon: Shield },
      'SUPPLIER_ADMIN': { label: 'Supplier Admin', color: 'bg-orange-100 text-orange-800', icon: Shield },
      'CONTRACTOR_USER': { label: 'Contractor User', color: 'bg-blue-50 text-blue-700', icon: Users },
      'EXHIBITOR_USER': { label: 'Exhibitor User', color: 'bg-green-50 text-green-700', icon: Users },
      'SUPPLIER_USER': { label: 'Supplier User', color: 'bg-orange-50 text-orange-700', icon: Users }
    };
    
    const config = roleConfig[role] || { label: role, color: 'bg-gray-100 text-gray-800', icon: Users };
    const Icon = config.icon;
    
    return (
      <Badge className={`gap-1 ${config.color} border-0 hover:${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Az önce';
    if (diffInHours < 24) return `${diffInHours} saat önce`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) return `${diffInDays} gün önce`;
    const diffInMonths = Math.floor(diffInDays / 30);
    return `${diffInMonths} ay önce`;
  };

  const handleBulkAction = (action) => {
    console.log(`Bulk action: ${action} on users:`, selectedUsers);
    // Implement bulk actions here
  };

  return (
    <div className="space-y-6 bg-gray-50 -m-3 sm:-m-6 p-3 sm:p-6 min-h-screen">
      {/* Page Header */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kullanıcı Yönetimi</h1>
            <p className="text-gray-600 mt-1">Tüm platform kullanıcılarını şirketlere göre yönetin</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Yenile
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Download className="h-4 w-4" />
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white shadow-sm border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  <p className="text-sm font-medium text-gray-600">Toplam Şirket</p>
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-1">{companies.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-green-600" />
                  <p className="text-sm font-medium text-gray-600">Toplam Kullanıcı</p>
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-1">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <UserCheck className="h-5 w-5 text-green-600" />
                  <p className="text-sm font-medium text-gray-600">Aktif Kullanıcı</p>
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-1">{users.filter(u => u.status === 'active').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <UserX className="h-5 w-5 text-red-600" />
                  <p className="text-sm font-medium text-gray-600">Pasif Kullanıcı</p>
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-1">{users.filter(u => u.status !== 'active').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Companies */}
        <div className="lg:col-span-1">
          <Card className="bg-white shadow-sm border">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold">Şirketler ({companies.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {/* Company Filters */}
              <div className="space-y-3 mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Şirket ara..."
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    className="pl-10 h-9 text-sm"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-9"
                  >
                    <option value="all">Tüm Durumlar</option>
                    <option value="active">Aktif</option>
                    <option value="trial">Deneme</option>
                    <option value="suspended">Askıda</option>
                  </select>
                  
                  <select
                    value={planFilter}
                    onChange={(e) => setPlanFilter(e.target.value)}
                    className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-9"
                  >
                    <option value="all">Tüm Planlar</option>
                    <option value="basic">Basic</option>
                    <option value="advance">Advance</option>
                    <option value="dynamic">Dynamic</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              </div>

              {/* Company List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <div key={i} className="animate-pulse p-3 border rounded-lg">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  ))
                ) : (
                  filteredCompanies.map((company) => (
                    <div
                      key={company.id}
                      onClick={() => setSelectedCompany(company)}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${
                        selectedCompany?.id === company.id ? 'bg-blue-50 border-blue-200' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate text-sm">{company.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500">{company.user_count} kullanıcı</span>
                            <Badge 
                              className={`text-xs ${
                                company.status === 'active' ? 'bg-green-100 text-green-800' :
                                company.status === 'trial' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              } border-0`}
                            >
                              {company.status === 'active' ? 'Aktif' : 
                               company.status === 'trial' ? 'Deneme' : 'Askıda'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Users */}
        <div className="lg:col-span-2">
          <Card className="bg-white shadow-sm border">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">
                  {selectedCompany ? selectedCompany.name : 'Şirket Seçiniz'}
                </CardTitle>
                {selectedCompany && (
                  <Badge variant="secondary" className="text-xs">
                    {filteredUsers.length} kullanıcı
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {selectedCompany ? (
                <>
                  {/* User Search */}
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        placeholder="Kullanıcı ara..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="pl-10 h-9 text-sm"
                      />
                    </div>
                  </div>

                  {/* Users Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">KULLANICI</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">ROL</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">ŞİFRE</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">DURUM</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">İŞLEMLER</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {loading ? (
                          [...Array(4)].map((_, i) => (
                            <tr key={i} className="animate-pulse">
                              <td className="py-3 px-4">
                                <div className="flex items-center space-x-3">
                                  <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                                  <div>
                                    <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
                                    <div className="h-3 bg-gray-200 rounded w-32"></div>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="h-4 bg-gray-200 rounded w-20"></div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="h-4 bg-gray-200 rounded w-6"></div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="h-5 bg-gray-200 rounded w-12"></div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="h-4 bg-gray-200 rounded w-6"></div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          filteredUsers.map((user) => (
                            <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                              <td className="py-3 px-4">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-sm">
                                    {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-900 text-sm">
                                      {user.first_name} {user.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500">{user.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-sm text-gray-700">{user.role}</span>
                              </td>
                              <td className="py-3 px-4">
                                <Eye className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600" />
                              </td>
                              <td className="py-3 px-4">
                                <Badge 
                                  className={`text-xs border-0 ${
                                    user.status === 'active' ? 'bg-green-100 text-green-800' :
                                    user.status === 'trial' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {user.status === 'active' ? 'Aktif' : 
                                   user.status === 'trial' ? 'Deneme' : 'Askıda'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                <Ban className="h-4 w-4 text-gray-400 cursor-pointer hover:text-red-500" />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Kullanıcıları görüntülemek için bir şirket seçin</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UltraUsers;